// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

// Connectivity detection + the sync trigger (#22): fires replayOutbox() on
// reconnect, on mount if already online with a non-empty queue, and on a
// short fallback timer (covers a missed/unreliable 'online' event) while
// anything is still pending. navigator.onLine is only used to decide *when
// to attempt* a replay — each request's own success/failure is still the
// real signal, since navigator.onLine is known to false-positive.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getFailedItems, getPendingCount, onReplayProgress, replayOutbox } from './outbox';

const FALLBACK_POLL_MS = 60_000;

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return isOnline;
}

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const isOnline = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [failedItems, setFailedItems] = useState([]);

  const refresh = useCallback(async () => {
    setPendingCount(await getPendingCount());
    setFailedItems(await getFailedItems());
  }, []);

  const syncNow = useCallback(async () => {
    await replayOutbox();
    await refresh();
  }, [refresh]);

  // Initial read + whenever a replay pass finishes, refresh the counts.
  useEffect(() => {
    refresh();
    return onReplayProgress((detail) => {
      if (detail.done) refresh();
    });
  }, [refresh]);

  // Trigger a replay on reconnect, and once on mount if already online.
  // syncNow is stable (memoized on refresh, which has no deps), so this
  // only actually re-runs when isOnline flips.
  useEffect(() => {
    if (isOnline) syncNow();
  }, [isOnline, syncNow]);

  // Fallback: some reconnects never fire a clean 'online' event. Only polls
  // while there's actually something to sync.
  useEffect(() => {
    if (!isOnline || pendingCount === 0) return undefined;
    const id = setInterval(syncNow, FALLBACK_POLL_MS);
    return () => clearInterval(id);
  }, [isOnline, pendingCount, syncNow]);

  return (
    <OfflineContext.Provider value={{ isOnline, pendingCount, failedItems, syncNow, refresh }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOfflineSync() {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error('useOfflineSync must be used inside <OfflineProvider>');
  return ctx;
}
